import { useEffect, useRef, useState } from "react";

function AdminPage({
  isLoggedIn,
  editingId,
  handleLogout,
  handleLogin,
  credentials,
  setCredentials,
  authError,
  form,
  setForm,
  aiPrompt,
  setAiPrompt,
  aiSuggestError,
  handleSuggestDraft,
  publishError,
  publishSuccess,
  isPublishing,
  handleSubmit,
  existingBannerImage,
  setExistingBannerImage,
  newBannerImage,
  handleBannerImageSelect,
  removeNewBannerImage,
  bannerFocus,
  setBannerFocus,
  imageFocus,
  setImageFocus,
  inlineImageUploads,
  handleInlineImageInsert,
  removeInlineImageUpload,
  handleImageSelect,
  existingImages,
  removeExistingImage,
  newImages,
  removeNewImage,
  resetForm,
}) {
  const contentInputRef = useRef(null);
  const lastBannerPreviewRef = useRef("");
  const imagePreviewCountRef = useRef(0);
  const [formatterColor, setFormatterColor] = useState("#1f78b4");
  const [focusModal, setFocusModal] = useState({
    open: false,
    type: "banner",
    preview: "",
  });

  const openFocusModal = (type, preview) => {
    if (!preview) return;
    setFocusModal({ open: true, type, preview });
  };

  const closeFocusModal = () => {
    setFocusModal((current) => ({ ...current, open: false }));
  };

  const onInlineImageSelect = (event) => {
    const files = event.target.files || [];
    if (!files.length) return;
    const selectionStart = contentInputRef.current?.selectionStart ?? form.content.length;
    const selectionEnd = contentInputRef.current?.selectionEnd ?? selectionStart;
    handleInlineImageInsert(files, selectionStart, selectionEnd);
    event.target.value = "";
  };

  const replaceSelection = (nextValue) => {
    setForm((current) => ({ ...current, content: nextValue }));
  };

  const applyWrapFormatter = (openTag, closeTag) => {
    const textarea = contentInputRef.current;
    if (!textarea) return;
    const content = form.content || "";
    const start = textarea.selectionStart ?? content.length;
    const end = textarea.selectionEnd ?? start;
    const selected = content.slice(start, end);
    const fallbackText = selected || "text";
    const wrapped = `${openTag}${fallbackText}${closeTag}`;
    const next = `${content.slice(0, start)}${wrapped}${content.slice(end)}`;
    replaceSelection(next);

    requestAnimationFrame(() => {
      textarea.focus();
      const caret = start + wrapped.length;
      textarea.setSelectionRange(caret, caret);
    });
  };

  const applyListFormatter = () => {
    const textarea = contentInputRef.current;
    if (!textarea) return;
    const content = form.content || "";
    const start = textarea.selectionStart ?? content.length;
    const end = textarea.selectionEnd ?? start;
    const selected = (content.slice(start, end) || "List item").trim();
    const lines = selected
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
    const listHtml = `<ul>${lines.map((line) => `<li>${line}</li>`).join("")}</ul>`;
    const next = `${content.slice(0, start)}${listHtml}${content.slice(end)}`;
    replaceSelection(next);

    requestAnimationFrame(() => {
      textarea.focus();
      const caret = start + listHtml.length;
      textarea.setSelectionRange(caret, caret);
    });
  };

  useEffect(() => {
    const preview = newBannerImage?.preview || "";
    if (preview && preview !== lastBannerPreviewRef.current) {
      setFocusModal({ open: true, type: "banner", preview });
    }
    lastBannerPreviewRef.current = preview;
  }, [newBannerImage?.preview]);

  useEffect(() => {
    if (newImages.length > imagePreviewCountRef.current && newImages.length > 0) {
      const latestPreview = newImages[newImages.length - 1]?.preview || "";
      if (latestPreview) {
        setFocusModal({ open: true, type: "image", preview: latestPreview });
      }
    }
    imagePreviewCountRef.current = newImages.length;
  }, [newImages]);

  useEffect(() => {
    if (!isLoggedIn) {
      setFocusModal({ open: false, type: "banner", preview: "" });
    }
  }, [isLoggedIn]);

  const activeFocus = focusModal.type === "banner" ? bannerFocus : imageFocus;
  const updateActiveFocus = (axis, value) => {
    const numeric = Math.max(0, Math.min(100, Number(value)));
    if (focusModal.type === "banner") {
      setBannerFocus((current) => ({ ...current, [axis]: numeric }));
    } else {
      setImageFocus((current) => ({ ...current, [axis]: numeric }));
    }
  };

  return (
    <main className="admin-main">
      {!isLoggedIn ? (
        <section className="admin-card login-card">
          <div className="login-layout">
            <div className="login-copy">
              <h2>
                Admin <span className="login-title-strong">Login</span>
              </h2>
              <p className="login-lead">
                Only Aksh Yadav can publish, edit, and remove blog posts.
              </p>
              <p className="login-note">Sign in to manage your portfolio stories.</p>
              <form className="form login-form" onSubmit={handleLogin}>
                <label>
                  Email
                  <input
                    type="email"
                    value={credentials.email}
                    onChange={(event) =>
                      setCredentials((current) => ({ ...current, email: event.target.value }))
                    }
                    placeholder="Enter admin email"
                    required
                  />
                </label>
                <label>
                  Password
                  <input
                    type="password"
                    value={credentials.password}
                    onChange={(event) =>
                      setCredentials((current) => ({ ...current, password: event.target.value }))
                    }
                    placeholder="Enter admin password"
                    required
                  />
                </label>
                {authError && <p className="error">{authError}</p>}
                <button className="btn-primary login-btn" type="submit">
                  Login
                </button>
              </form>
            </div>
            <aside className="login-illustration" aria-hidden="true">
              <svg viewBox="0 0 360 280" role="img">
                <defs>
                  <linearGradient id="gradA" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#72bdea" />
                    <stop offset="100%" stopColor="#2c82bf" />
                  </linearGradient>
                  <linearGradient id="gradB" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#edf7ff" />
                    <stop offset="100%" stopColor="#d7ecfb" />
                  </linearGradient>
                </defs>
                <rect x="12" y="16" width="336" height="248" rx="22" fill="url(#gradA)" opacity="0.95" />
                <rect x="38" y="48" width="284" height="36" rx="10" fill="#ffffff" opacity="0.24" />
                <rect x="38" y="102" width="222" height="16" rx="8" fill="url(#gradB)" opacity="0.94" />
                <rect x="38" y="130" width="260" height="16" rx="8" fill="url(#gradB)" opacity="0.82" />
                <rect x="38" y="158" width="180" height="16" rx="8" fill="url(#gradB)" opacity="0.72" />
                <circle cx="296" cy="191" r="36" fill="#d8efff" opacity="0.84" />
                <circle cx="288" cy="186" r="16" fill="#2c82bf" />
                <path d="M278 214c11 10 29 10 40 0" stroke="#2c82bf" strokeWidth="7" strokeLinecap="round" />
              </svg>
              <p>Secure editor access for writing, updating, and curating posts.</p>
            </aside>
          </div>
        </section>
      ) : (
        <section className="admin-editor">
          <div className="admin-head">
            <h2>{editingId ? "Edit Blog Post" : "Create Blog Post"}</h2>
            <button className="btn-secondary" type="button" onClick={handleLogout}>
              Logout
            </button>
          </div>

          <div className="ai-box">
            <h3>
              AI Suggestor
              <span className="mini-illustration" aria-hidden="true">
                <svg viewBox="0 0 52 20" role="img">
                  <rect x="1" y="2" width="50" height="16" rx="8" fill="#d9efff" />
                  <circle cx="15" cy="10" r="4" fill="#2c82bf" />
                  <circle cx="26" cy="10" r="4" fill="#58a9da" />
                  <circle cx="37" cy="10" r="4" fill="#80c0e5" />
                </svg>
              </span>
            </h3>
            <p>Enter any topic prompt and get a quick starter draft for your post.</p>
            <div className="ai-controls">
              <input
                type="text"
                value={aiPrompt}
                onChange={(event) => setAiPrompt(event.target.value)}
                placeholder="Example: fitness, data science, a trip to china"
              />
              <button className="btn-secondary" type="button" onClick={handleSuggestDraft}>
                Suggest Draft
              </button>
            </div>
            {aiSuggestError && <p className="error">{aiSuggestError}</p>}
          </div>

          <form className="form" onSubmit={handleSubmit}>
            <label>
              Blog Post Title
              <input
                type="text"
                value={form.title}
                onChange={(event) =>
                  setForm((current) => ({ ...current, title: event.target.value }))
                }
                placeholder="Write a strong title"
                required
              />
            </label>
            <label>
              Categories (tags)
              <input
                type="text"
                value={form.categories}
                onChange={(event) =>
                  setForm((current) => ({ ...current, categories: event.target.value }))
                }
                placeholder="Learning, React, Fitness"
              />
            </label>

            <label>
              Content
              <div className="format-toolbar">
                <button type="button" onClick={() => applyWrapFormatter("<strong>", "</strong>")}>
                  B
                </button>
                <button type="button" onClick={() => applyWrapFormatter("<em>", "</em>")}>
                  I
                </button>
                <button type="button" onClick={() => applyWrapFormatter("<u>", "</u>")}>
                  U
                </button>
                <button type="button" onClick={applyListFormatter}>
                  Bullets
                </button>
                <input
                  type="color"
                  value={formatterColor}
                  onChange={(event) => setFormatterColor(event.target.value)}
                  aria-label="Pick text color"
                />
                <button
                  type="button"
                  onClick={() =>
                    applyWrapFormatter(
                      `<span style="color:${formatterColor}">`,
                      "</span>"
                    )
                  }
                >
                  Color
                </button>
              </div>
              <textarea
                ref={contentInputRef}
                rows={10}
                value={form.content}
                onChange={(event) =>
                  setForm((current) => ({ ...current, content: event.target.value }))
                }
                placeholder="Write the blog content"
                required
              />
              <p className="inline-help">Formatting from toolbar will be rendered in final blog view.</p>
            </label>

            <div className="inline-image-box">
              <p className="preview-label">Inline Images (insert anywhere in content)</p>
              <p className="inline-help">
                Place your cursor in the content box, then upload images. They will be inserted
                exactly at that position.
              </p>
              <input type="file" accept="image/*" multiple onChange={onInlineImageSelect} />
              {inlineImageUploads.length > 0 && (
                <div className="image-preview-grid">
                  {inlineImageUploads.map((item, index) => (
                    <div className="preview-card" key={`${item.key}-${item.preview}`}>
                      <img src={item.preview} alt={`Inline upload ${index + 1}`} />
                      <button
                        className="btn-danger"
                        type="button"
                        onClick={() => removeInlineImageUpload(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label>
              Links
              <input
                type="text"
                value={form.links}
                onChange={(event) =>
                  setForm((current) => ({ ...current, links: event.target.value }))
                }
                placeholder="https://github.com/project | https://demo.site"
              />
            </label>

            <label>
              Banner Image (shown below title)
              <input type="file" accept="image/*" onChange={handleBannerImageSelect} />
            </label>
            <div className="btn-row">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => openFocusModal("banner", newBannerImage?.preview || existingBannerImage)}
              >
                Adjust Banner View
              </button>
            </div>
            {(existingBannerImage || newBannerImage?.preview) && (
              <div className="image-preview-group">
                <p className="preview-label">Banner preview</p>
                <div className="image-preview-grid">
                  {existingBannerImage && (
                    <div className="preview-card">
                      <img
                        src={existingBannerImage}
                        alt="Existing banner"
                        style={{ objectPosition: `${bannerFocus.x}% ${bannerFocus.y}%` }}
                      />
                      <button
                        className="btn-danger"
                        type="button"
                        onClick={() => setExistingBannerImage("")}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {newBannerImage?.preview && (
                    <div className="preview-card">
                      <img
                        src={newBannerImage.preview}
                        alt="New banner preview"
                        style={{ objectPosition: `${bannerFocus.x}% ${bannerFocus.y}%` }}
                      />
                      <button
                        className="btn-danger"
                        type="button"
                        onClick={removeNewBannerImage}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <label>
              Images
              <input type="file" accept="image/*" multiple onChange={handleImageSelect} />
            </label>
            <div className="btn-row">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => openFocusModal("image", newImages?.[0]?.preview || existingImages?.[0] || "")}
              >
                Adjust Image View
              </button>
            </div>
            {existingImages.length > 0 && (
              <div className="image-preview-group">
                <p className="preview-label">Existing images</p>
                <div className="image-preview-grid">
                  {existingImages.map((image, index) => (
                    <div className="preview-card" key={`${image}-${index}`}>
                      <img
                        src={image}
                        alt={`Existing blog visual ${index + 1}`}
                        style={{ objectPosition: `${imageFocus.x}% ${imageFocus.y}%` }}
                      />
                      <button
                        className="btn-danger"
                        type="button"
                        onClick={() => removeExistingImage(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {newImages.length > 0 && (
              <div className="image-preview-group">
                <p className="preview-label">New uploads</p>
                <div className="image-preview-grid">
                  {newImages.map((item, index) => (
                    <div className="preview-card" key={`${item.file.name}-${item.preview}`}>
                      <img
                        src={item.preview}
                        alt={`Upload preview ${index + 1}`}
                        style={{ objectPosition: `${imageFocus.x}% ${imageFocus.y}%` }}
                      />
                      <button
                        className="btn-danger"
                        type="button"
                        onClick={() => removeNewImage(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="btn-row">
              <button className="btn-primary" type="submit" disabled={isPublishing}>
                {isPublishing
                  ? "Saving..."
                  : editingId
                    ? "Update Post"
                    : "Publish Post"}
              </button>
              <button className="btn-secondary" type="button" onClick={resetForm}>
                Clear
              </button>
            </div>
            {publishError && <p className="error">{publishError}</p>}
            {publishSuccess && <p className="success-text">{publishSuccess}</p>}
          </form>

          {focusModal.open && (
            <div className="focus-modal-backdrop" role="dialog" aria-modal="true">
              <div className="focus-modal">
                <h3>{focusModal.type === "banner" ? "Adjust Banner Focus" : "Adjust Image Focus"}</h3>
                <p>Drag the sliders to choose which visible area should stay in frame.</p>
                <div
                  className={`focus-preview ${focusModal.type === "banner" ? "focus-preview-banner" : "focus-preview-image"}`}
                >
                  <img
                    src={focusModal.preview}
                    alt="Focus preview"
                    style={{ objectPosition: `${activeFocus.x}% ${activeFocus.y}%` }}
                  />
                </div>
                <div className="focus-controls">
                  <label>
                    Horizontal ({Math.round(activeFocus.x)}%)
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={activeFocus.x}
                      onChange={(event) => updateActiveFocus("x", event.target.value)}
                    />
                  </label>
                  <label>
                    Vertical ({Math.round(activeFocus.y)}%)
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={activeFocus.y}
                      onChange={(event) => updateActiveFocus("y", event.target.value)}
                    />
                  </label>
                </div>
                <div className="btn-row">
                  <button className="btn-primary" type="button" onClick={closeFocusModal}>
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </main>
  );
}

export default AdminPage;
